import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import { Input, DatePicker, Button, Badge, Select } from 'antd';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from 'controllers/notificationController';
import moment from 'moment';
import { getAllOrdersAction, getAllSubscriptionAction } from '@app/store/slices/userSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import styled from 'styled-components';
import { BaseSpace } from '@app/components/common/BaseSpace/BaseSpace';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useNavigate } from 'react-router-dom';

const { RangePicker } = DatePicker;
const { Option } = Select;

export const BasicTable: React.FC = () => {
  const [filters, setFilters] = useState({
    clientName: '',
    deliveryDateRange: [],
    deliveryStatus: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [tableData, setTableData] = useState({ data: [], loading: false, total: 0 });
  const dispatch = useAppDispatch();
  let navigate = useNavigate();
  // Fetch data with updated filters and pagination
  const fetchData = useCallback(() => {
    setTableData((prev) => ({ ...prev, loading: true }));

    const params = {
      ...filters,
      deliveryDateRange: filters.deliveryDateRange.length
        ? [
          moment(filters.deliveryDateRange[0]).toISOString(),
          moment(filters.deliveryDateRange[1]).toISOString(),
        ]
        : [],
      current: pagination.current,
      pageSize: pagination.pageSize,
    };

    dispatch(getAllSubscriptionAction(params)).unwrap()
      .then((response) => {
        setTableData({
          data: response.data,
          loading: false,
          total: response.total,
        });
      })
      .catch((error) => {
        notificationController.error({ message: error.message || 'Failed to fetch data' });
        setTableData((prev) => ({ ...prev, loading: false }));
      });
  }, [pagination, filters, dispatch]);



  useEffect(() => {
    function hideError(e) {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div'
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay'
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    }

    window.addEventListener('error', hideError)
    return () => {
      window.addEventListener('error', hideError)
    }
  }, [])

  // Trigger fetch when filters or pagination change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange = (filterName: string, value: any) => {
    setFilters((prev) => ({ ...prev, [filterName]: value }));
  };

  // Handle table pagination change
  const handleTableChange = (paginationInfo) => {
    setPagination({ current: paginationInfo.current, pageSize: paginationInfo.pageSize });
  };

  const columns: ColumnsType<any> = [
      {
          title: 'Subscription ID',
          dataIndex: 'subscription_id',
          render: (text) => <span>{text || 'Not available'}</span>,
          filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
              <Input
                placeholder="Search Subscription ID"
                value={selectedKeys[0]}
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedKeys(value ? [value] : []);
                  handleFilterChange('subscription_id', value); // Update the filter state
                }}
                onPressEnter={() => confirm()}
                style={{ width: 188, marginBottom: 8, display: 'block' }}
              />
              <Button
                type="primary"
                onClick={() => confirm()}
                size="small"
                style={{ width: 90, marginRight: 8 }}
              >
                Search
              </Button>
              <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
                Reset
              </Button>
            </div>
          ),
        },
    {
      title: 'Client Name',
      dataIndex: 'client_name',
      render: (text, record) => <span>{record?.client?.name || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Client Name"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('clientName', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => record?.client?.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Client Mobile',
      dataIndex: 'client_mobile',
      render: (text, record) => <span>{record?.client?.phone || 'Not available'}</span>,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Client Mobile"
            value={selectedKeys[0]}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedKeys(value ? [value] : []);
              handleFilterChange('clientPhone', value); // Update the filter state
            }}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 90, marginRight: 8 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      onFilter: (value, record) => record?.client?.phone.includes(value),
    },
    {
      title: 'Client Wallet Balance',
      dataIndex: 'client_wallet_mobile',
      render: (text, record) => <span>{record?.client?.balance || 'Not available'}</span>,
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      render: (text, record) => <span>{record?.product_details?.name || 'Not available'}</span>,
    },
    {
      title: 'Product Size',
      dataIndex: 'product_size',
      render: (text, record) => <span>{record?.product_details?.isVariantEnabled ?
        record?.product_details?.selectedOption?.quantity + ' '+record?.product_details?.selectedOption?.unit :
        record?.product_details?.quantity + ' '+ record?.product_details?.unit}</span>,
    },
    {
      title: 'Quantity Ordered',
      dataIndex: 'quantity_ordered',
      render: (text, record) => <span>{record?.quantity || 'Not available'}</span>,
    },
    // {
    //   title: 'Delivery Date',
    //   dataIndex: 'delivery_date',
    //   render: (text, record) =>
    //     record?.delivery_dates?.[0]?.date
    //       ? moment(record.delivery_dates[0].date).format('DD-MMM-YYYY')
    //       : 'Not available',
    //   filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    //     <div style={{ padding: 8 }}>
    //       <RangePicker
    //         value={selectedKeys[0] ? selectedKeys : []}
    //         onChange={(dates) => {
    //           setSelectedKeys(dates || []);
    //           handleFilterChange('deliveryDateRange', dates || []); // Update the filter state
    //         }}
    //         style={{ width: 188, marginBottom: 8, display: 'block' }}
    //       />
    //       <Button
    //         type="primary"
    //         onClick={() => confirm()}
    //         size="small"
    //         style={{ width: 90, marginRight: 8 }}
    //       >
    //         Search
    //       </Button>
    //       <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
    //         Reset
    //       </Button>
    //     </div>
    //   ),
    // },
    {
      title: 'Start Date',
      dataIndex: 'start_date',
      render: (text, record) => (
        <span>{record.start_date ? moment(record?.start_date).format('DD-MMM-YYYY') : ""}</span>
      ),

    },
    {
      title: 'End Date',
      dataIndex: 'end_date',
      render: (text, record) => (
        <span>{record.end_date ? moment(record?.end_date).format('DD-MMM-YYYY') : ""}</span>
      ),

    },

    // {
    //   title: 'Total Amount',
    //   dataIndex: 'total_amount',
    //   render: (text) => <span>{text || 'Not available'}</span>,
    // },


    {
      title: 'Plan Type',
      dataIndex: 'plan_type',
      render: (text, record) => <span>{record.plan_id == 1 ? "Alternate" : record.plan_id == 2 ? "Daily" : record.plan_id == 3 ? "Weekly" : ""}</span>,
    },
    {
      title: "Actions",
      dataIndex: 'actions',
      width: '15%',
      render: (text: string, record: any) => {
        return (
          <BaseSpace>
            <BaseButton
              type="ghost"
              onClick={() => {
                navigate('/admin/subscriptions/deliveries/' + record._id)
                // notificationController.info({ message: t('tables.inviteMessage', { name: record.name }) });
              }}
            >
              View Deliveries
            </BaseButton>
            {/* <BaseButton
              type="ghost"
              onClick={() => {
                navigate('/company/patients/charts/list')
                // notificationController.info({ message: t('tables.inviteMessage', { name: record.name }) });
              }}
            >
              Manage Charts
            </BaseButton> */}
          </BaseSpace>
        );
      },
    },
  ];

  return (
    <>
      <Button
        style={{ marginBottom: '20px' }}
        onClick={() => {
          setTableData((prev) => ({ ...prev, loading: true }));
          setFilters({
            clientName: '',
            deliveryDateRange: [],
            deliveryStatus: ''
          });

          setTimeout(() => {
            setPagination({ current: 1, pageSize: 20 });

          }, 1000);
        }}>Clear Filters</Button>
      <BaseTable
        columns={columns}
        dataSource={tableData.data}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: tableData.total,
        }}
        loading={tableData.loading}
        onChange={handleTableChange}
        scroll={{ x: 800 }}
        bordered
      />
    </>
  );
};
